import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/api/axios';
import {
  Sparkles,
  TrendingUp,
  Activity,
  Calendar,
  Brain,
  X,
  Settings,
  ChevronDown,
  ChevronUp,
  RefreshCw,
} from 'lucide-react';
import type { Pet } from '../../types/pet';
import { AIInsightCard, AIInsight } from './AIInsightCard';
import { AIShareControls } from './AIShareControls';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface AIHealthInsightsProps {
  pets: Pet[];
  initialPet: Pet;
  aiEnabled: boolean;
  onClose: () => void;
}

// Mock insights data
const generateMockInsights = (_petId: string): AIInsight[] => {
  return [
    {
      id: 'insight-1',
      category: 'growth',
      title: 'Healthy Weight Progression',
      summary:
        'Weight has increased steadily over the last 3 months, consistent with expected growth patterns.',
      details:
        'Based on 6 weight measurements, your pet is maintaining a healthy growth trajectory. The average monthly gain is 0.5 kg, which aligns with breed standards for this age group.',
      trend: 'stable',
      confidence: 'high',
      dataPoints: [
        'Weight logs (6 entries)',
        'Breed standards',
        'Age-appropriate ranges',
      ],
      actionSuggestion: 'Maintain current diet and exercise routine',
      updatedAt: new Date().toISOString(),
    },
    {
      id: 'insight-2',
      category: 'preventive',
      title: 'Vaccination Schedule On Track',
      summary:
        'All core vaccinations are current. Next booster due in 45 days.',
      details:
        "Rabies, DHPP, and Bordetella vaccinations are up to date. Based on your pet's age and previous vaccination dates, the next recommended booster is scheduled for mid-January.",
      trend: 'improving',
      confidence: 'high',
      dataPoints: [
        'Vaccination records (4 vaccines)',
        'Veterinary visit history',
        'Age-based protocols',
      ],
      actionSuggestion: 'Schedule appointment for January booster',
      updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'insight-3',
      category: 'behavioral',
      title: 'Grooming Tolerance Improving',
      summary:
        'Professional notes indicate increased cooperation during recent grooming sessions.',
      details:
        'Over the last 4 grooming appointments, handling notes show reduced stress indicators and improved behavior. This suggests successful desensitization to grooming procedures.',
      trend: 'improving',
      confidence: 'medium',
      dataPoints: [
        'Grooming records (4 sessions)',
        'Professional handling notes',
        'Behavioral observations',
      ],
      actionSuggestion:
        'Continue regular grooming schedule to maintain progress',
      updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'insight-4',
      category: 'lifestyle',
      title: 'Regular Activity Pattern Detected',
      summary:
        'Consistent appointment frequency suggests active wellness management.',
      details:
        'Your pet has attended appointments every 4-6 weeks on average, including veterinary checkups, grooming, and wellness visits. This regular engagement supports long-term health.',
      trend: 'stable',
      confidence: 'high',
      dataPoints: [
        'Appointment history (12 visits)',
        'Visit frequency analysis',
        'Wellness patterns',
      ],
      updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'insight-5',
      category: 'risk',
      title: 'Annual Dental Checkup Approaching',
      summary: 'It has been 11 months since last dental examination.',
      details:
        'Based on last dental visit date and breed-specific recommendations, a dental checkup is recommended. Early detection of dental issues can prevent more serious health problems.',
      trend: 'needs-attention',
      confidence: 'medium',
      dataPoints: [
        'Last dental visit (11 months ago)',
        'Breed dental risk factors',
        'Age considerations',
      ],
      actionSuggestion:
        'Consider scheduling a dental examination with your veterinarian',
      updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    },
  ];
};

// Mock weight data for chart
const weightData = [
  { month: 'Jun', weight: 22.5 },
  { month: 'Jul', weight: 23.0 },
  { month: 'Aug', weight: 23.8 },
  { month: 'Sep', weight: 24.2 },
  { month: 'Oct', weight: 24.8 },
  { month: 'Nov', weight: 25.3 },
  { month: 'Dec', weight: 25.7 },
];

export function AIHealthInsights({
  pets,
  initialPet,
  aiEnabled,
  onClose,
}: AIHealthInsightsProps) {
  const { t } = useTranslation();
  const [selectedPetId, setSelectedPetId] = useState<string>(
    initialPet?.id || pets[0]?.id || ''
  );
  const selectedPet = useMemo(
    () => pets.find((p) => p.id === selectedPetId) || initialPet || pets[0],
    [pets, selectedPetId, initialPet]
  );

  // Fetch active AI insight type config from backend
  const [activeInsightConfig, setActiveInsightConfig] = useState<
    { category: string; confidenceThreshold: number }[] | null
  >(null);

  useEffect(() => {
    apiClient
      .get<{ items: { category: string; confidenceThreshold: number; enabled: boolean }[] }>(
        '/ai-governance/insight-types/active'
      )
      .then((res) => setActiveInsightConfig(res.data.items))
      .catch(() => setActiveInsightConfig(null)); // Fallback: show all
  }, []);

  const CONFIDENCE_MAP: Record<string, number> = {
    high: 0.9,
    medium: 0.7,
    low: 0.5,
  };

  const allInsights = useMemo(
    () => generateMockInsights(selectedPet?.id || ''),
    [selectedPet?.id]
  );

  // Filter insights by active categories and confidence threshold
  const insights = useMemo(() => {
    if (!activeInsightConfig) return allInsights; // Fallback: show all
    const enabledCategories = new Map(
      activeInsightConfig.map((c) => [c.category, c.confidenceThreshold])
    );
    return allInsights.filter((insight) => {
      const threshold = enabledCategories.get(insight.category);
      if (threshold === undefined) return false; // Category not enabled
      const numericConfidence = CONFIDENCE_MAP[insight.confidence] ?? 0.5;
      return numericConfidence >= threshold;
    });
  }, [allInsights, activeInsightConfig]);
  const insightKeyPrefix: Record<string, string> = {
    'insight-1': 'insightHealthyWeight',
    'insight-2': 'insightVaccination',
    'insight-3': 'insightGrooming',
    'insight-4': 'insightActivity',
    'insight-5': 'insightDental',
  };
  const translatedInsights = useMemo(
    () =>
      insights.map((i) => {
        const prefix = insightKeyPrefix[i.id];
        if (!prefix) return i;
        return {
          ...i,
          title: t(`features.aiInsights.${prefix}Title`),
          summary: t(`features.aiInsights.${prefix}Summary`),
          details: t(`features.aiInsights.${prefix}Details`),
          dataPoints: (() => {
            const translated = t(`features.aiInsights.${prefix}DataPoints`, {
              returnObjects: true,
              defaultValue: i.dataPoints,
            }) as unknown;
            return Array.isArray(translated) ? translated : i.dataPoints;
          })(),
          actionSuggestion: i.actionSuggestion
            ? t(`features.aiInsights.${prefix}Action`)
            : undefined,
        };
      }),
    [insights, t]
  );
  const [showShareControls, setShowShareControls] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedSummary, setExpandedSummary] = useState(true);
  const [insightFeedback, setInsightFeedback] = useState<
    Record<string, boolean>
  >({});

  const handleFeedback = (insightId: string, helpful: boolean) => {
    setInsightFeedback((prev) => ({
      ...prev,
      [insightId]: helpful,
    }));
  };

  const filteredInsights =
    selectedCategory === 'all'
      ? translatedInsights
      : translatedInsights.filter((i) => i.category === selectedCategory);

  const overallTrend: 'stable' | 'improving' | 'needs-attention' = 'stable';
  const lastUpdated = new Date();

  const categories = [
    {
      id: 'all',
      label: t('features.aiInsights.allInsights', 'All Insights'),
      count: insights.length,
    },
    {
      id: 'growth',
      label: t('features.aiInsights.growthWeight', 'Growth & Weight'),
      count: insights.filter((i) => i.category === 'growth').length,
    },
    {
      id: 'preventive',
      label: t('features.aiInsights.preventiveCare', 'Preventive Care'),
      count: insights.filter((i) => i.category === 'preventive').length,
    },
    {
      id: 'behavioral',
      label: t('features.aiInsights.behavioral', 'Behavioral'),
      count: insights.filter((i) => i.category === 'behavioral').length,
    },
    {
      id: 'lifestyle',
      label: t('features.aiInsights.lifestyle', 'Lifestyle'),
      count: insights.filter((i) => i.category === 'lifestyle').length,
    },
    {
      id: 'risk',
      label: t('features.aiInsights.riskIndicators', 'Risk Indicators'),
      count: insights.filter((i) => i.category === 'risk').length,
    },
  ];

  if (!aiEnabled) {
    return (
      <div className="fixed inset-0 bg-white dark:bg-[#16151D] z-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-gray-100 dark:bg-[#0F0E16] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-10 h-10 text-gray-400 dark:text-white/40" />
          </div>
          <h2
            className="text-xl mb-2 text-gray-900 dark:text-white"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            {t(
              'features.aiInsights.notActivated',
              'AI Health Insights Not Activated'
            )}
          </h2>
          <p
            className="text-gray-600 dark:text-white/70 mb-6"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('features.aiInsights.enableForPet', {
              name: selectedPet?.name || '',
            })}
          </p>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 dark:hover:from-blue-600 dark:hover:to-purple-600 transition-all"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
          >
            {t('features.aiInsights.goBack', 'Go Back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-white dark:bg-[#16151D] z-50 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 text-white z-10">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-12 h-12 bg-white/20 dark:bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h1
                  className="text-2xl"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.aiInsights.title', 'AI Health Insights')}
                </h1>
                {pets.length <= 1 && (
                  <p
                    className="text-blue-100 dark:text-blue-200 text-sm"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('features.aiInsights.forPet', {
                      name: selectedPet?.name || '',
                    })}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {pets.length > 1 && (
                <Select value={selectedPetId} onValueChange={setSelectedPetId}>
                  <SelectTrigger
                    className="w-[130px] h-9 bg-white/20 dark:bg-white/10 border-white/30 dark:border-white/20 text-white hover:bg-white/30 dark:hover:bg-white/20 [&>span]:text-white [&>span]:truncate"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <SelectValue placeholder={t('common.pet', 'Pet')} />
                  </SelectTrigger>
                  <SelectContent
                    style={{
                      backgroundColor: 'var(--surface)',
                      borderColor: 'var(--border)',
                    }}
                  >
                    {pets.map((p) => (
                      <SelectItem
                        key={p.id}
                        value={p.id}
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <span className="mr-2">
                          {p.species === 'dog'
                            ? '🐕'
                            : p.species === 'cat'
                              ? '🐈'
                              : '🐾'}
                        </span>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <button
                onClick={() => setShowShareControls(true)}
                className="p-2 bg-white/20 hover:bg-white/30 dark:bg-white/10 dark:hover:bg-white/20 rounded-lg transition-colors"
                title={t(
                  'features.aiInsights.sharingSettings',
                  'Sharing Settings'
                )}
              >
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 bg-white/20 hover:bg-white/30 dark:bg-white/10 dark:hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Summary Card */}
          <div className="bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/20 dark:border-white/10">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3
                    className="text-white"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                  >
                    {t(
                      'features.aiInsights.overallHealthTrend',
                      'Overall Health Trend'
                    )}
                  </h3>
                  <button
                    onClick={() => setExpandedSummary(!expandedSummary)}
                    className="p-1 hover:bg-white/10 rounded"
                  >
                    {expandedSummary ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-3 py-1 rounded-full text-sm ${
                      overallTrend === 'improving'
                        ? 'bg-green-400/20 text-green-100 border border-green-400/30'
                        : overallTrend === 'stable'
                          ? 'bg-blue-400/20 text-blue-100 border border-blue-400/30'
                          : 'bg-amber-400/20 text-amber-100 border border-amber-400/30'
                    }`}
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {overallTrend === 'improving'
                      ? `📈 ${t('features.aiInsights.improving', 'Improving')}`
                      : overallTrend === 'stable'
                        ? `➡️ ${t('features.aiInsights.stable', 'Stable')}`
                        : `⚠️ ${t('features.aiInsights.needsAttention', 'Needs Attention')}`}
                  </span>
                  <span
                    className="text-sm text-blue-100 dark:text-blue-200"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('features.aiInsights.lastUpdated', 'Last updated')}:{' '}
                    {lastUpdated.toLocaleDateString()}
                  </span>
                </div>
              </div>
              <button
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title={t(
                  'features.aiInsights.refreshInsights',
                  'Refresh insights'
                )}
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>

            {expandedSummary && (
              <div className="grid grid-cols-4 gap-3 mt-4">
                <div className="bg-white/10 dark:bg-white/5 rounded-lg p-3 border border-white/20 dark:border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-green-300 dark:text-green-400" />
                    <span
                      className="text-sm text-blue-100 dark:text-blue-200"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('features.aiInsights.improving', 'Improving')}
                    </span>
                  </div>
                  <p
                    className="text-2xl text-white"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {insights.filter((i) => i.trend === 'improving').length}
                  </p>
                </div>
                <div className="bg-white/10 dark:bg-white/5 rounded-lg p-3 border border-white/20 dark:border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <Activity className="w-4 h-4 text-blue-300 dark:text-blue-400" />
                    <span
                      className="text-sm text-blue-100 dark:text-blue-200"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('features.aiInsights.stable', 'Stable')}
                    </span>
                  </div>
                  <p
                    className="text-2xl text-white"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {insights.filter((i) => i.trend === 'stable').length}
                  </p>
                </div>
                <div className="bg-white/10 dark:bg-white/5 rounded-lg p-3 border border-white/20 dark:border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4 text-amber-300 dark:text-amber-400" />
                    <span
                      className="text-sm text-blue-100 dark:text-blue-200"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('features.aiInsights.actionNeeded', 'Action Needed')}
                    </span>
                  </div>
                  <p
                    className="text-2xl text-white"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {
                      insights.filter((i) => i.trend === 'needs-attention')
                        .length
                    }
                  </p>
                </div>
                <div className="bg-white/10 dark:bg-white/5 rounded-lg p-3 border border-white/20 dark:border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <Brain className="w-4 h-4 text-purple-300 dark:text-purple-400" />
                    <span
                      className="text-sm text-blue-100 dark:text-blue-200"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('features.aiInsights.totalInsights', 'Total Insights')}
                    </span>
                  </div>
                  <p
                    className="text-2xl text-white"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {translatedInsights.length}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Weight Trend Chart */}
        <div className="bg-white dark:bg-[#0F0E16] rounded-xl border border-gray-200 dark:border-white/8 p-6 mb-6">
          <h3
            className="mb-4 text-gray-900 dark:text-white"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
          >
            {t(
              'features.aiInsights.weightTrendTitle',
              'Weight Trend (Last 6 Months)'
            )}
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={weightData}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e5e7eb"
                className="dark:opacity-20"
              />
              <XAxis
                dataKey="month"
                stroke="#6b7280"
                className="dark:stroke-white/50"
              />
              <YAxis
                stroke="#6b7280"
                className="dark:stroke-white/50"
                domain={[20, 27]}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--tooltip-bg, #fff)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                }}
                formatter={(value: number) => [
                  `${value} kg`,
                  t('features.aiInsights.growthWeight', 'Weight'),
                ]}
              />
              <Line
                type="monotone"
                dataKey="weight"
                stroke="url(#gradient)"
                strokeWidth={3}
                dot={{ fill: '#3b82f6', r: 4 }}
              />
              <defs>
                <linearGradient id="gradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#9333ea" />
                </linearGradient>
              </defs>
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Category Filter */}
        <div className="mb-6">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 text-white'
                    : 'bg-gray-100 dark:bg-[#0F0E16] text-gray-700 dark:text-white/90 hover:bg-gray-200 dark:hover:bg-white/10 border dark:border-white/8'
                }`}
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {cat.label} ({cat.count})
              </button>
            ))}
          </div>
        </div>

        {/* Insights Grid */}
        <div className="space-y-4 mb-6">
          {filteredInsights.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-[#0F0E16] rounded-xl border border-gray-200 dark:border-white/8">
              <Brain className="w-12 h-12 text-gray-400 dark:text-white/40 mx-auto mb-3" />
              <p
                className="text-gray-600 dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t(
                  'features.aiInsights.noInsightsInCategory',
                  'No insights available in this category yet.'
                )}
              </p>
              <p
                className="text-sm text-gray-500 dark:text-white/50 mt-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t(
                  'features.aiInsights.checkBackAfterData',
                  'Check back after more data is collected.'
                )}
              </p>
            </div>
          ) : (
            filteredInsights.map((insight) => (
              <AIInsightCard
                key={insight.id}
                insight={{ ...insight, helpful: insightFeedback[insight.id] }}
                variant="owner"
                onFeedback={handleFeedback}
                showActions={true}
              />
            ))
          )}
        </div>

        {/* Medical Disclaimer */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/30 rounded-xl p-4 mb-6">
          <p
            className="text-sm text-amber-900 dark:text-amber-200"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <span className="font-medium">
              {t(
                'features.aiInsights.medicalDisclaimerLabel',
                'Medical Disclaimer'
              )}
              :{' '}
            </span>
            {t('features.aiInsights.medicalDisclaimer')}
          </p>
        </div>
      </div>

      {/* Share Controls Modal */}
      {showShareControls && (
        <AIShareControls
          petName={selectedPet?.name || ''}
          onClose={() => setShowShareControls(false)}
        />
      )}
    </div>
  );
}
